import { i18n } from '@/lib/i18n'
import type { AutomationRule, FlowConfig } from '@/hooks/use-automation'

/**
 * "Quy tắc nhãn" (kiểu Lark: Nếu … thì Thêm vào nhãn) KHÔNG có engine riêng —
 * mỗi quy tắc là một luồng Tự động hoá bình thường:
 *
 *   Có tin nhắn đến  ─(lọc "Chỉ khi" = các điều kiện)→  Gắn nhãn
 *
 * nên chạy chung hàng đợi, nhật ký, quyền `automation.*` với mọi luồng khác và
 * vẫn xem/sửa được ở màn Tự động hoá. `metadata.kind = 'tag-rule'` đánh dấu để
 * màn Nhãn nhận lại đúng các luồng của mình.
 */
export const TAG_RULE_KIND = 'tag-rule'

export type TagRuleLogic = 'and' | 'or'

export interface TagRuleCondition {
  field: string
  op: string
  value: string
}

export interface TagRuleDraft {
  /** id luồng Tự động hoá; bỏ trống = quy tắc mới chưa lưu. */
  ruleId?: string
  logic: TagRuleLogic
  conditions: TagRuleCondition[]
  /** Tên nhãn gắn cho khách (nhãn lưu theo TÊN trên Contact.tags). */
  tag: string
  enabled: boolean
}

/** Trường điều kiện — khớp các khoá engine nạp cho trigger message_received. */
export const TAG_RULE_FIELDS: { value: string; label: string }[] = [
  { value: 'message', get label() { return i18n.t('convContact:tagRules.fields.message') } },
  { value: 'contact.fullName', get label() { return i18n.t('convContact:tagRules.fields.fullName') } },
  { value: 'contact.phone', get label() { return i18n.t('convContact:tagRules.fields.phone') } },
  { value: 'contact.email', get label() { return i18n.t('convContact:tagRules.fields.email') } },
  { value: 'contact.source', get label() { return i18n.t('convContact:tagRules.fields.source') } },
  { value: 'contact.tags', get label() { return i18n.t('convContact:tagRules.fields.tags') } },
]

export const TAG_RULE_OPS: { value: string; label: string; noValue?: boolean }[] = [
  { value: 'contains', get label() { return i18n.t('convContact:tagRules.ops.contains') } },
  { value: 'not_contains', get label() { return i18n.t('convContact:tagRules.ops.not_contains') } },
  { value: 'eq', get label() { return i18n.t('convContact:tagRules.ops.eq') } },
  { value: 'neq', get label() { return i18n.t('convContact:tagRules.ops.neq') } },
  { value: 'exists', get label() { return i18n.t('convContact:tagRules.ops.exists') }, noValue: true },
  { value: 'not_exists', get label() { return i18n.t('convContact:tagRules.ops.not_exists') }, noValue: true },
]

export const opNeedsValue = (op: string) => !TAG_RULE_OPS.find((o) => o.value === op)?.noValue

export function emptyCondition(): TagRuleCondition {
  return { field: 'message', op: 'contains', value: '' }
}

export function emptyTagRule(tag: string): TagRuleDraft {
  return { logic: 'and', conditions: [emptyCondition()], tag, enabled: true }
}

export function isTagRule(rule: AutomationRule): boolean {
  const fc = rule.flowConfig as FlowConfig | null | undefined
  return fc?.metadata?.kind === TAG_RULE_KIND
}

/** Luồng Tự động hoá → dạng hiển thị. Trả null nếu luồng đã bị sửa khác khuôn. */
export function ruleToDraft(rule: AutomationRule): TagRuleDraft | null {
  const fc = rule.flowConfig as FlowConfig | null | undefined
  const action = fc?.nodes.find((n) => n.actionType === 'add_tag')
  const tag = action?.config?.tag
  if (!fc || typeof tag !== 'string') return null
  const rules = (fc.trigger.config?.filterRules as TagRuleCondition[] | undefined) ?? []
  return {
    ruleId: rule.id,
    logic: fc.trigger.config?.filterLogic === 'or' ? 'or' : 'and',
    conditions: rules.map((r) => ({ field: r.field, op: r.op, value: r.value == null ? '' : String(r.value) })),
    tag,
    enabled: rule.enabled,
  }
}

/** Dạng hiển thị → flowConfig lưu xuống Tự động hoá. */
export function draftToFlowConfig(d: TagRuleDraft): FlowConfig {
  return {
    version: '2',
    trigger: {
      id: 'trigger',
      type: 'message_received',
      label: 'Có tin nhắn đến', // i18n-ignore
      config: {
        filterRules: d.conditions.map((c) => ({ field: c.field, op: c.op, value: opNeedsValue(c.op) ? c.value.trim() : '' })),
        filterLogic: d.logic,
      },
      position: { x: 40, y: 40 },
    },
    nodes: [
      {
        id: 'tag',
        type: 'action',
        actionType: 'add_tag',
        label: 'Gắn nhãn', // i18n-ignore
        config: { tag: d.tag },
        status: 'active',
        position: { x: 40, y: 200 },
      },
    ],
    edges: [{ source: 'trigger', target: 'tag' }],
    metadata: { kind: TAG_RULE_KIND },
  }
}

/** Lỗi nhập liệu của một quy tắc (null = hợp lệ). */
export function validateDraft(d: TagRuleDraft): string | null {
  if (!d.tag) return i18n.t('convContact:tagRules.errors.pickTag')
  if (d.conditions.length === 0) return i18n.t('convContact:tagRules.errors.needCondition')
  if (d.conditions.some((c) => opNeedsValue(c.op) && !c.value.trim())) return i18n.t('convContact:tagRules.errors.needKeyword')
  return null
}

/** Tên luồng hiện ở màn Tự động hoá — đọc là biết đây là quy tắc nhãn nào. */
export const ruleName = (tag: string) => `Tự gắn nhãn: ${tag}` // i18n-ignore
